'use client';

import { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { ListingCard } from '@/components/cards/ListingCard';
import { IconChevronLeft, IconChevronRight } from '@repo/svgs';
import type { Message, Listing } from '@repo/api';
import { analyticsDataAttrs } from '@/lib/analytics/data-attrs';
import { urgencyParam } from '@/lib/listings/urgency';

interface ListingsCarouselProps {
  message: Message;
  isMessageWindow?: boolean;
}

export function ListingsCarousel({ message, isMessageWindow = false }: ListingsCarouselProps) {
  // Type guard: contentData should be Listing[] when contentType is LISTINGS
  const listings: Listing[] = Array.isArray(message.contentData) ? message.contentData : [];
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 336; // Width of card (320px) + gap (16px)
    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  // Update scroll button states and handle wheel events
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial check after a brief delay to ensure layout is complete
    const timeoutId = setTimeout(() => {
      updateScrollButtons();
    }, 100);

    const handleScroll = () => {
      updateScrollButtons();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Handle wheel events for horizontal scrolling - similar to old implementation
    // Only prevent default if we're actually scrolling horizontally
    const handleWheel = (e: WheelEvent) => {
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;

      // Only handle if horizontal scroll is more significant than vertical AND container can scroll horizontally
      if (hasHorizontalScroll && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        e.stopPropagation();
        container.scrollBy({
          left: e.deltaX,
          behavior: 'auto',
        });
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    // Check on resize
    const handleResize = () => {
      updateScrollButtons();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      window.removeEventListener('resize', handleResize);
    };
  }, [listings.length]);

  if (listings.length === 0) return null;

  return (
    <div className="mt-2 relative w-full min-w-0">
      {/* Overlay navigation buttons (like legacy UI) */}
      <div className="md:block hidden">
        {listings.length > 1 && (
          <>
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={cn(
                // Center vertically to the ListingCard image area (w-80 with 3:2 image => ~213px tall)
                'absolute left-0 top-[106px] -translate-y-1/2 z-10',
                'flex h-8 w-8 items-center justify-center rounded-full border border-secondary bg-primary text-secondary',
                'hover:bg-secondary transition-colors',
                'disabled:bg-secondary disabled:!cursor-not-allowed disabled:border-transparent',
              )}
              aria-label="Scroll left"
            >
              <IconChevronLeft className="w-3 h-3" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={cn(
                // Center vertically to the ListingCard image area (w-80 with 3:2 image => ~213px tall)
                'absolute right-0 top-[106px] -translate-y-1/2 z-10',
                'flex h-8 w-8 items-center justify-center rounded-full border border-secondary bg-primary text-secondary',
                'hover:bg-secondary transition-colors',
                'disabled:bg-secondary disabled:!cursor-not-allowed disabled:border-transparent',
              )}
              aria-label="Scroll right"
            >
              <IconChevronRight className="w-3 h-3" />
            </button>
          </>
        )}
      </div>

      {/* Scroll Container */}
      <div
        ref={scrollContainerRef}
        className={cn(
          'flex gap-4 overflow-x-auto overflow-y-hidden hide-scrollbar snap-x snap-mandatory min-w-0 w-full mx-auto touch-pan-x overscroll-x-contain',
          isMessageWindow ? 'md:!w-[84%]' : 'md:max-w-80',
        )}
        style={{
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {listings.map((listing: Listing, index: number) => (
          <div
            key={listing.id || index}
            className={cn(
              'flex-shrink-0 snap-start md:w-full',
              isMessageWindow
                ? listings.length > 1
                  ? '!w-[82%]'
                  : '!w-[100%]'
                : listings.length > 1
                  ? 'w-[84%] md:max-w-[280px]'
                  : 'w-[100%] md:max-w-[280px]',
              // Mobile-only edge padding: first card gets left padding, last card gets right padding.
              // If there's only 1 card, it's both first & last, so it gets both paddings.
              index === 0 && (isMessageWindow ? 'pl-17 md:pl-0' : 'pl-15 md:pl-0'),
              index === listings.length - 1 &&
                (isMessageWindow ? 'pr-17 md:pr-0' : 'pr-15 md:pr-0'),
            )}
          >
            <ListingCard
              listing={listing}
              showOnlyOneImage={true}
              {...analyticsDataAttrs('similar_listings_unitcard', {
                unitId: listing.slug,
                screen: 'message',
                chat_source: listing.activeListing?.isFutureListing
                  ? 'future'
                  : listing.isVerified
                    ? 'current_verified'
                    : 'current_non_verified',
                ...urgencyParam(listing),
              })}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
